import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

// ── Wave 2 / E2.1 — Customers & Customer Portal ───────────────────

export interface Customer {
  id: string
  tenant_id: string
  name: string
  contact_name: string
  contact_phone: string
  contact_email: string
  billing_address: string
  tax_id: string
  notes: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CustomerPortalConfig {
  tenant_id: string
  portal_slug: string
  custom_domain: string
  logo_url: string
  brand_color: string
  accent_color: string
  show_eta: boolean
  show_driver_name: boolean
  show_vehicle_plate: boolean
  allow_reschedule: boolean
  allow_feedback: boolean
  welcome_message: string
  support_phone: string
  support_email: string
  is_published: boolean
}

export interface CustomerFeedback {
  id: string
  job_id: string
  customer_id?: string | null
  rating: number
  comment: string
  submitted_at: string
}

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { data?: T[] } | undefined
  return o?.data ?? []
}

export function useCustomers() {
  return useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get('/customers').then((r) => unwrap<Customer>(r.data)),
  })
}

export function useCreateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<Customer>) => api.post<Customer>('/customers', input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  })
}

export function useUpdateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...rest }: Partial<Customer> & { id: string }) =>
      api.put<Customer>(`/customers/${id}`, rest).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  })
}

export function useDeleteCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  })
}

export function usePortalConfig() {
  return useQuery({
    queryKey: ['customer-portal-config'],
    queryFn: () => api.get<CustomerPortalConfig>('/customer-portal/config').then((r) => r.data),
  })
}

export function useUpdatePortalConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<CustomerPortalConfig>) =>
      api.put<CustomerPortalConfig>('/customer-portal/config', input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-portal-config'] }),
  })
}

export function useFeedback() {
  return useQuery({
    queryKey: ['customer-feedback'],
    queryFn: () => api.get('/customer-feedback').then((r) => unwrap<CustomerFeedback>(r.data)),
  })
}

export function useCreateJobShare() {
  return useMutation({
    mutationFn: ({ jobId, expires_hours }: { jobId: string; expires_hours?: number }) =>
      api.post<{ token: string; url: string; expires_at: string }>(`/jobs/${jobId}/share`, { expires_hours }).then((r) => r.data),
  })
}

// ── Wave 2 / E2.4 — Rate cards & invoices ─────────────────────────

export interface RateCard {
  id: string
  customer_id: string | null
  name: string
  pricing_model: 'per_km' | 'per_stop' | 'per_hour' | 'per_delivery' | 'flat'
  unit_rate: number
  currency: string
  minimum_charge: number
  free_units: number
  is_active: boolean
  effective_from: string | null
  effective_to: string | null
  notes: string
}

export interface CustomerInvoice {
  id: string
  customer_id: string | null
  number: string
  amount: number
  currency: string
  status: 'draft' | 'sent' | 'paid' | 'void'
  due_at: string | null
  generated_at: string
  paid_at: string | null
}

export interface VehiclePnL {
  id: string
  device_id: string
  period: string
  revenue: number
  fuel_cost: number
  maintenance_cost: number
  driver_cost: number
  other_cost: number
  distance_km: number
  trips_count: number
  contribution_margin: number
  currency: string
}

export function useRateCards(customerId?: string) {
  return useQuery({
    queryKey: ['rate-cards', customerId ?? null],
    queryFn: () =>
      api
        .get('/rate-cards', { params: customerId ? { customer_id: customerId } : {} })
        .then((r) => unwrap<RateCard>(r.data)),
  })
}

export function useCreateRateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<RateCard>) => api.post<RateCard>('/rate-cards', input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rate-cards'] }),
  })
}

export function useDeleteRateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/rate-cards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rate-cards'] }),
  })
}

export function useCustomerInvoices(status?: string) {
  return useQuery({
    queryKey: ['customer-invoices', status ?? 'all'],
    queryFn: () =>
      api
        .get('/customer-invoices', { params: status ? { status } : {} })
        .then((r) => unwrap<CustomerInvoice>(r.data)),
  })
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/customer-invoices/${id}/paid`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-invoices'] }),
  })
}

export function useGenerateInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (customerId: string) =>
      api.post<CustomerInvoice>(`/customers/${customerId}/invoices/generate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-invoices'] }),
  })
}

export function useVehiclePnL() {
  return useQuery({
    queryKey: ['vehicle-pnl'],
    queryFn: () => api.get('/vehicle-pnl').then((r) => unwrap<VehiclePnL>(r.data)),
  })
}

// ── Wave 2 / E2.5 — Cold chain ────────────────────────────────────

export interface TemperatureProfile {
  id: string
  device_id: string
  zone_label: string
  sensor_key: string
  target_min_c: number
  target_max_c: number
  hysteresis_c: number
  min_duration_sec: number
  regulation: string
  is_active: boolean
}

export interface TemperatureExcursion {
  id: string
  device_id: string
  profile_id: string
  zone_label: string
  direction: 'above' | 'below'
  started_at: string
  ended_at: string | null
  peak_value_c: number
  duration_sec: number | null
  acknowledged: boolean
  note: string
}

export function useTemperatureProfiles(deviceId?: string) {
  return useQuery({
    queryKey: ['temperature-profiles', deviceId ?? null],
    queryFn: () =>
      api
        .get('/temperature-profiles', { params: deviceId ? { device_id: deviceId } : {} })
        .then((r) => unwrap<TemperatureProfile>(r.data)),
  })
}

export function useUpsertTemperatureProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<TemperatureProfile>) =>
      api.post<TemperatureProfile>('/temperature-profiles', input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['temperature-profiles'] }),
  })
}

export function useTemperatureExcursions(unackOnly = false) {
  return useQuery({
    queryKey: ['temperature-excursions', unackOnly],
    queryFn: () =>
      api
        .get('/temperature-excursions', { params: unackOnly ? { unack: true } : {} })
        .then((r) => unwrap<TemperatureExcursion>(r.data)),
  })
}

export function useAcknowledgeExcursion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post(`/temperature-excursions/${id}/ack`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['temperature-excursions'] }),
  })
}

// ── Wave 2 / E2.3 — Fuel cards & approvals ────────────────────────

export interface FuelCard {
  id: string
  provider: string
  account_ref: string
  nickname: string
  is_active: boolean
  last_sync_at: string | null
}

export interface PendingExpense {
  id: string
  driver_user_id: string
  category: string
  amount: number
  currency: string
  occurred_at: string
  receipt_url: string
  notes: string
}

export function useFuelCards() {
  return useQuery({
    queryKey: ['fuel-cards'],
    queryFn: () => api.get('/fuel-cards').then((r) => unwrap<FuelCard>(r.data)),
  })
}

export function useCreateFuelCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<FuelCard>) => api.post<FuelCard>('/fuel-cards', input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fuel-cards'] }),
  })
}

export function usePendingExpenses() {
  return useQuery({
    queryKey: ['pending-expenses'],
    queryFn: () => api.get('/expenses/pending').then((r) => unwrap<PendingExpense>(r.data)),
  })
}

export function useDecideExpense() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, approve, reason }: { id: string; approve: boolean; reason?: string }) =>
      api.post(`/expenses/${id}/decision`, { approve, reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-expenses'] }),
  })
}
